/**
 * Notification code taxonomy.
 *
 * The device encodes each event as a numeric string where code[0] is the event
 * type and code[2] is the compartment number. This mirrors exactly the filters
 * the Notifications page has always applied.
 */

export const EVENT_TYPES = [
  { id: "taken", digit: "7", label: "Dose taken", tone: "good" },
  { id: "missed", digit: "6", label: "Dose missed", tone: "critical" },
  { id: "wrong", digit: "1", label: "Wrong compartment", tone: "warning" },
  { id: "low", digit: "3", label: "Low pills", tone: "warning" },
  { id: "empty", digit: "2", label: "Out of pills", tone: "critical" },
  { id: "other", digit: null, label: "Other", tone: "neutral" },
];

const BY_DIGIT = EVENT_TYPES.reduce((map, type) => {
  if (type.digit) map[type.digit] = type;
  return map;
}, {});

const OTHER = EVENT_TYPES[EVENT_TYPES.length - 1];

export const BOX_COUNT = 8;

/** The event type for a notification, falling back to "Other". */
export const eventType = (notification) => BY_DIGIT[String(notification?.code || "")[0]] || OTHER;

/** Compartment number 1-8, or null when the code carries none. */
export const boxNumber = (notification) => {
  const digit = Number(String(notification?.code || "")[2]);
  return digit >= 1 && digit <= BOX_COUNT ? digit : null;
};

export const isTaken = (notification) => eventType(notification).id === "taken";
export const isMissed = (notification) => eventType(notification).id === "missed";

/** Every type enabled — the default filter state. */
export const allTypesEnabled = () =>
  EVENT_TYPES.reduce((acc, type) => ({ ...acc, [type.id]: true }), {});

/** Every compartment enabled — the default filter state. */
export const allBoxesEnabled = () =>
  Array.from({ length: BOX_COUNT }, (_, i) => i + 1).reduce((acc, box) => ({ ...acc, [box]: true }), {});

/**
 * Apply the type and box filters, then sort. Same semantics as the pre-revamp
 * page: a notification survives when its type is enabled, and — if its code
 * names a compartment — when that compartment is enabled too.
 */
export const filterAndSort = (notifications, { types, boxes, descending }) => {
  const filtered = notifications.filter((notification) => {
    if (!types[eventType(notification).id]) return false;
    const box = boxNumber(notification);
    if (box && !boxes[box]) return false;
    return true;
  });
  return filtered.sort((a, b) =>
    descending
      ? new Date(b.timestamp) - new Date(a.timestamp)
      : new Date(a.timestamp) - new Date(b.timestamp)
  );
};

export const countActiveFilters = ({ types, boxes }) => {
  const typesOff = EVENT_TYPES.filter((type) => !types[type.id]).length;
  const boxesOff = Object.values(boxes).filter((on) => !on).length;
  return typesOff + boxesOff;
};
